import { DocumentsSignatureStatusEnum } from 'generated/resources/graphql'

import SafeLocalStorage from 'modules/core/lib/SafeLocalStorage'
import { apiClient } from 'modules/core/lib/mobx-rest'

import {
  DOCUMENT_SIGNATURE_BOUNCED_EMAIL,
  DOCUMENT_SIGNATURE_CANCELLED,
  DOCUMENT_SIGNATURE_COMPLETED,
  DOCUMENT_SIGNATURE_DECLINED,
  DOCUMENT_SIGNATURE_ERROR,
  DOCUMENT_SIGNATURE_EXPIRED,
  DOCUMENT_SIGNATURE_PARTIALLY_SIGNED,
  DOCUMENT_SIGNATURE_PENDING,
  ESIGNATURE_FILE_TYPE,
  LOCALSTORAGE_DOCUMENTS_SUBFOLDER_COUNT,
  SPACES,
} from './constants'

/** Minimal document shape accepted by utility functions. Both the legacy
 *  `Document` type (from useGetDocuments) and `DocumentNode` (from the
 *  DataCollection query) satisfy this interface. */
export interface DocumentLike {
  _policies?: {
    delete?: { can: boolean }
    edit?: { can: boolean }
  } | null
  signatureStatus?: DocumentsSignatureStatusEnum | string | null
  status?: string | null
  space?: string
  templateId?: string | null
  filename?: string | null
  fromTemplate?: unknown
  employee?: { id: string } | null
  deletedAt?: unknown
  contentType?: string | null
  folder?: { id: string } | null
}

export function isPublicCompanyDocument(document: DocumentLike) {
  return document.space === SPACES.COMPANY_PUBLIC
}

export function isPendingToFill(document: DocumentLike) {
  return document.status === 'to_fill'
}

export function isTemplate(document: DocumentLike): boolean {
  return !!document.templateId || !!document.fromTemplate
}

function canUpdateDocument(document: DocumentLike) {
  return document._policies?.edit?.can ?? false
}

export function canUpdateDocuments(documents: DocumentLike[]) {
  return documents.every(canUpdateDocument)
}

export function canDeleteDocument(document: DocumentLike) {
  return document._policies?.delete?.can ?? false
}

export function canDeleteDocuments(documents: DocumentLike[]) {
  return documents.every(canDeleteDocument)
}

export function isPendingSignature(document: DocumentLike) {
  return document.signatureStatus === 'pending' || document.signatureStatus === 'partially_signed'
}

export function isDeclinedSignatures(document: DocumentLike) {
  return document.signatureStatus === 'declined'
}

export function isPendingSignatures(document: DocumentLike) {
  return document.signatureStatus === 'pending'
}

export function isCancelledSignatures(document: DocumentLike) {
  return document.signatureStatus === 'cancelled'
}

export function isBouncedSignatures(document: DocumentLike) {
  return document.signatureStatus === 'bounced_email'
}

export function isPartiallySignedSignatures(document: DocumentLike) {
  return document.signatureStatus === 'partially_signed'
}

export function isErrorSignatures(document: DocumentLike) {
  return document.signatureStatus === 'error'
}

export function isExpiredSignatures(document: DocumentLike) {
  return document.signatureStatus === 'expired'
}

export function isCompanyInternalSpace(space: string) {
  return space === SPACES.COMPANY_INTERNAL
}

export function isCompanyPublicSpace(space: string) {
  return space === SPACES.COMPANY_PUBLIC
}

export function isCompanySpace(space: string) {
  return isCompanyInternalSpace(space) || isCompanyPublicSpace(space)
}

export function canFill(document: DocumentLike, employeeId: string) {
  return isPendingToFill(document) && document.employee?.id === employeeId
}

export function isInTrashBin(document: DocumentLike) {
  return document.deletedAt !== undefined && document.deletedAt !== null
}

export function isSignable(document: DocumentLike) {
  return document.contentType === ESIGNATURE_FILE_TYPE
}

export function downloadDocuments(ids: string[]) {
  return apiClient().post(
    '/documents/download_documents',
    { ids: ids.join(',') },
    { responseType: 'blob' }
  )
}

export function setSubfoldersCountForSkeletons(nSubfolders: number, folderId: string | undefined) {
  try {
    const folderSubfolders = JSON.parse(
      SafeLocalStorage.getItem(LOCALSTORAGE_DOCUMENTS_SUBFOLDER_COUNT, '[]') || '[]'
    )

    const selectedFolder = folderSubfolders.filter(
      (folder: { id: string; n: number }) => folder.id === folderId
    )[0]

    if (selectedFolder) {
      selectedFolder.n = nSubfolders
    } else {
      folderSubfolders.push({ id: folderId, n: nSubfolders })
    }

    SafeLocalStorage.setItem(
      LOCALSTORAGE_DOCUMENTS_SUBFOLDER_COUNT,
      JSON.stringify(folderSubfolders)
    )
  } catch (e) {
    SafeLocalStorage.setItem(LOCALSTORAGE_DOCUMENTS_SUBFOLDER_COUNT, JSON.stringify([]))
  }
}

export function getSubfoldersCountForSkeletons(folderId: string | undefined) {
  try {
    if (folderId) {
      const folderSubfolders = JSON.parse(
        SafeLocalStorage.getItem(LOCALSTORAGE_DOCUMENTS_SUBFOLDER_COUNT, '[]') || '[]'
      )

      return (
        folderSubfolders.filter((folder: { id: string; n: number }) => folder.id === folderId)[0]
          .n ?? 7
      )
    }
  } catch (e) {
    SafeLocalStorage.setItem(LOCALSTORAGE_DOCUMENTS_SUBFOLDER_COUNT, JSON.stringify([]))
  }
  return 7
}

export function documentSignatureStatusLabel(status: DocumentsSignatureStatusEnum) {
  switch (status) {
    case 'pending':
      return DOCUMENT_SIGNATURE_PENDING()
    case 'partially_signed':
      return DOCUMENT_SIGNATURE_PARTIALLY_SIGNED()
    case 'completed':
      return DOCUMENT_SIGNATURE_COMPLETED()
    case 'declined':
      return DOCUMENT_SIGNATURE_DECLINED()
    case 'bounced_email':
      return DOCUMENT_SIGNATURE_BOUNCED_EMAIL()
    case 'cancelled':
      return DOCUMENT_SIGNATURE_CANCELLED()
    case 'error':
      return DOCUMENT_SIGNATURE_ERROR()
    case 'expired':
      return DOCUMENT_SIGNATURE_EXPIRED()
    default:
      return ''
  }
}
